<template>
	<AsiDialog :open="open" :title="getnameTranslated(title)" @cancel="cancel" :icon="icons.info" scrollable>
		<div class="d-flex flex-column flex-gap">
			<div v-for="(group, index) in groups" :key="`group-${index}`">
				<span class="font-weight-bold">
					{{ getnameTranslated(group.title) }}
				</span>

				<v-simple-table dense class="mt-2" :key="`table-${index}`">
					<colgroup>
						<col />
						<col style="width: 30%" />
						<col style="width: 20%" />
					</colgroup>

					<tbody>
						<tr
							v-for="(line, lineIndex) in group.lines"
							:key="`${group.title}-${line.lineName}-${line.fromQuantity}-${lineIndex}`"
						>

							<td
								:colspan="getColspanForFirstLine(line)"
								style="vertical-align: top;"
								class="pt-1"
							>
								{{ getnameTranslated(line.lineName) }}
							</td>

							<td v-if="line.fromQuantity !== null" class="text-left">
								{{ $t('frameAssembly.terms.fromPiece', [line.fromQuantity]) }}
							</td>

							<td
								:colspan="line.fromQuantity === null ? 2 : 1"
								class="text-right"
							>
								{{
									$n(finalAmount(line.price), {
										key: 'currencyDisplay',
										currency: line.price.currency.currencyCode
									})
								}}
							</td>

						</tr>
					</tbody>
				</v-simple-table>
			</div>
		</div>
	</AsiDialog>
</template>

<script lang="ts">
	import {Component, Emit} from 'vue-property-decorator';
	import AsiDialogForm from "@/components/common/AsiDialogForm.vue";
	import AsiDialog from "@/components/common/AsiDialog.vue";
	import {IPrice} from "@/models/price/PriceModels";
	import TotalHelper from "@/helpers/TotalHelper";
	import {ITranslatedValue} from "@/models/translated-value/TranslatedValueModels";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import Icon from "@/plugins/icons";

	class PriceGroup {
		title!: ITranslatedValue;
		lines: PriceLine[] = [];
	}

	class PriceLine {
		lineName: ITranslatedValue | null = null;
		fromQuantity: number | null = null;
		price!: IPrice;
	}

	@Component({
		components: {AsiDialog}
	})
	export default class FrameAssemblyPriceTableDialog extends AsiDialogForm {

		public icons = Icon;

		public get groups(): PriceGroup[] {
			return [
				{
					title: {
						de: 'Rahmenfertigung',
						fr: 'Fabrication de cadres',
						it: 'Produzione telai',
						en: 'Frame Production'
					},
					lines: [
						{
							lineName: {
								de: 'Rahmen geklebt',
								fr: 'Cadre collé',
								it: 'Telaio incollato',
								en: 'Glued Frame'
							},
							fromQuantity: 1,
							price: {
								amount: 9.20,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},


						{
							lineName: null,
							fromQuantity: 30,
							price: {
								amount: 8.90,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 50,
							price: {
								amount: 8.50,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 80,
							price: {
								amount: 8.10,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 100,
							price: {
								amount: 7.50,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 150,
							price: {
								amount: 7.10,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 200,
							price: {
								amount: 6.50,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 250,
							price: {
								amount: 6.10,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						}
					]
				},
				{
					title: {
						de: 'C-Rahmen, U-Rahmen, Eckstücke',
						fr: 'Cadres en C, cadres en U, pièces d\'angle',
						it: 'Telai a C, telai a U, pezzi d\'angolo',
						en: 'C-Frames, U-Frames, Corner Pieces'
					},
					lines: [
						{
							lineName: {
								de: 'C-Rahmen geklebt',
								fr: 'Cadre en C collé',
								it: 'Telaio a C incollato',
								en: 'C-frame, glued'
							},
							fromQuantity: 1,
							price: {
								amount: 5.50,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: {
								de: 'U-Rahmen geklebt',
								fr: 'Cadre en U collé',
								it: 'Telaio a U incollato',
								en: 'U-frame, glued'
							},
							fromQuantity: 1,
							price: {
								amount: 5.50,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: {
								de: 'Eckstücke geklebt, 100x100mm',
								fr: 'Pièces d\'angle collées, 100x100mm',
								it: 'Pezzi d\'angolo incollati, 100x100mm',
								en: 'Corner pieces, glued, 100x100mm'
							},
							fromQuantity: 1,
							price: {
								amount: 2.30,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						}
					]
				},
				{
					title: {
						de: 'Einrichtkosten pauschal',
						fr: 'Frais d’installation forfaitaires',
						it: 'Costi di allestimento forfettari',
						en: 'Set-up Costs'
					},
					lines: [
						{
							lineName: null,
							fromQuantity: 1,
							price: {
								amount: 40,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 10,
							price: {
								amount: 30,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 20,
							price: {
								amount: 20,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 40,
							price: {
								amount: 10,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: null,
							fromQuantity: 60,
							price: {
								amount: 0,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						}
					]
				},
				{
					title: {
						de: 'Zuschläge',
						fr: 'Suppléments',
						it: 'Supplementi',
						en: 'Surcharges'
					},
					lines: [
						{
							lineName: {
								de: 'Schrägrahmen (pro Stück)',
								fr: 'Cadre oblique (per piece)',
								it: 'Telaio obliquo (per pezzo)',
								en: 'Angled frame (per piece)'
							},
							fromQuantity: null,
							price: {
								amount: 20,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						},
						{
							lineName: {
								de: 'Ausmessen von Dichtungsrahmen (pro Stück)',
								fr: 'Mesurage de cadres d\'étanchéité (per piece)',
								it: 'Misurazione di telai di tenuta (per pezzo)',
								en: 'Measuring of seal frames (per piece)'
							},
							fromQuantity: null,
							price: {
								amount: 20,
								currency: {
									currencyCode: 'CHF',
									symbol: null
								},
								vat: 8.1
							}
						}
					]
				}
			];
		}

		private get title(): ITranslatedValue {
			return {
				de: 'PREISE Rahmenkonfektion',
				fr: 'PRIX Confection de cadres',
				it: 'PREZZI Confezionamento telai',
				en: 'PRICES Frame Assembly'
			};
		}

		private get amountsInclVat(): boolean {
			return this.$store.state.ui.amountsInclVat;
		}

		private getColspanForFirstLine(line: PriceLine): number {
			if (line.lineName === null && line.fromQuantity === null) return 2;
			if (line.lineName === null || line.fromQuantity === null) return 1;

			return 0;
		}

		private getnameTranslated(name: ITranslatedValue): string | null {
			return TranslatedValueHelper.get(name, this.$i18n.locale);
		}

		private finalAmount(price: IPrice): number {
			return price.amount + (this.amountsInclVat ? TotalHelper.vatAmount(price) : 0);
		}

		@Emit('cancel')
		public cancel(event: Event): Event {
			return event;
		}
	}
</script>

<style scoped lang="scss">
.flex-gap {
	gap: 3rem;
}
</style>